import { useState, useEffect, lazy } from 'react';
import { ChevronDown } from 'lucide-react';
import Button from '../components/Button';
import MobileFallback from '../three/MobileFallback';
import ScrollAnimator from '../three/ScrollAnimator';

const HeroScene = lazy(() => import('../three/HeroScene'));

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);
  return isMobile;
}

export default function Hero() {
  const isMobile = useIsMobile();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    // Delay 3D mount slightly so the page paints text first
    const t = setTimeout(() => setMounted(true), 100);
    return () => clearTimeout(t);
  }, []);

  return (
    <>
      <ScrollAnimator />
      <section
        id="hero"
        className="relative min-h-screen flex items-center overflow-hidden"
      >
        {/* 3D Background */}
        <div id="hero-canvas" className="absolute inset-0">
          {isMobile ? (
            <MobileFallback />
          ) : (
            mounted && <HeroScene />
          )}
        </div>

        {/* Vignette overlay */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              'radial-gradient(ellipse at center, transparent 30%, rgba(10,10,15,0.6) 100%)',
          }}
        />
        {/* Bottom fade for section transition */}
        <div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-t from-[#0A0A0F] to-transparent pointer-events-none" />

        {/* Content */}
        <div
          id="hero-content"
          className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 sm:pt-32"
        >
          <div className="lg:w-3/5">
            {/* Badge */}
            <div className="flex">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full glass mb-8 text-sm text-gray-400">
                <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                Launching Soon
              </div>
            </div>

            {/* Headline */}
            <h1 className="font-display font-bold text-5xl sm:text-6xl lg:text-7xl tracking-tight text-left mb-6 leading-[1.1]">
              <span className="block text-white">Hire Fast.</span>
              <span className="block text-gradient">Pay Crypto.</span>
              <span className="block">
                <span className="text-white">Stay </span>
                <span className="text-gradient-bold">Anonymous.</span>
              </span>
            </h1>

            {/* Subtitle */}
            <p className="text-lg text-gray-400 max-w-lg mb-10 leading-relaxed text-left">
              The first privacy-first hiring platform for OFM teams, VAs, and
              chatters. Verified workers, crypto escrow, zero exposure.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row items-start gap-4">
              <Button variant="gradient" size="lg" href="#cta">
                Start Hiring
              </Button>
              <Button variant="text" size="lg" href="#cta" className="sm:mt-2">
                Find Work →
              </Button>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10">
          <a
            href="#how-it-works"
            className="flex flex-col items-center gap-2 text-gray-500 hover:text-gray-300 transition-colors"
          >
            <span className="text-xs uppercase tracking-widest">Scroll</span>
            <ChevronDown size={20} className="animate-scroll-hint" />
          </a>
        </div>
      </section>
    </>
  );
}
